import React, { useCallback, useMemo } from 'react';
import { Box, Heading, Text, Button, TextButton } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { useAtomValue } from 'jotai';
import { Footer } from '../../../components/Footer';
import s from './MobileDomain.scss';
import { ArrowRight, ChevronRightSmall } from '@wix/wix-ui-icons-common';
import { DomainInput } from '../components/DomainInput';
import { availableDomainAtom, domainSuggestionsAtom } from '../atoms';
import { useDomainLogic } from '../hooks/useDomainLogic';
import { BI_CONSTANTS } from '../../../hooks/useBiLogger/biConstants';
import { useBiLogger } from '../../../hooks/useBiLogger/useBiLogger';
import { DynamicSelector } from '../../../components/DynamicSelector/DynamicSelector';

export function MobileDomain() {
  const { t } = useTranslation();
  const availableDomain = useAtomValue(availableDomainAtom);
  const suggestions = useAtomValue(domainSuggestionsAtom);
  const { search, selectedSuggestion, onContinue } = useDomainLogic();
  const biLogger = useBiLogger();

  const suggestionOptions = useMemo(() => {
    return suggestions.map((domain) => ({
      value: domain,
      label: domain,
    }));
  }, [suggestions]);

  const onContinueClicked = () => {
    biLogger.actionClicked({
      action: BI_CONSTANTS.ACTION.DOMAIN_PAGE_NEXT,
      button_name: BI_CONSTANTS.BUTTON_NAME.NEXT,
      value: availableDomain,
    });
    onContinue({});
  };

  const onSkipClicked = () => {
    biLogger.actionClicked({
      action: BI_CONSTANTS.ACTION.DOMAIN_PAGE_SKIP,
      button_name: BI_CONSTANTS.BUTTON_NAME.SKIP,
    });
    onContinue({ skip: true });
  };

  const onSuggestionSelect = useCallback(
    (option: { value: string }) => {
      biLogger.actionClicked({
        action: BI_CONSTANTS.ACTION.DOMAIN_PAGE_SUGGESTION_SELECT,
        button_name: BI_CONSTANTS.BUTTON_NAME.SELECT,
        value: option.value,
      });
      selectedSuggestion.onSelect(option.value);
    },
    [biLogger, selectedSuggestion],
  );

  return (
    <Box direction="vertical" height="100%" width="100%" gap={2} className={s.container}>
      <Box direction="vertical" className={s.contentWrapper} gap={4}>
        <Box direction="vertical" gap={1}>
          <Heading className={s.title} size="medium">
            {t('DOMAIN_PAGE_TITLE')}
          </Heading>
          <Text>{t('DOMAIN_PAGE_SUBTITLE')}</Text>
        </Box>

        <DomainInput value={search.value} onChange={search.onChange} />

        {suggestionOptions.length ? (
          <DynamicSelector
            label={t('DOMAIN_PAGE_MORE_AVAILABLE_DOMAINS')}
            selectedValue={selectedSuggestion.value}
            onSelect={onSuggestionSelect}
            options={suggestionOptions}
          />
        ) : null}
      </Box>

      <Footer
        className={s.templateFooter}
        button={
          <Box width="100%" direction="vertical" gap={2}>
            {availableDomain && (
              <Text weight="bold" skin="success" size="medium">
                {t('DOMAIN_PAGE_DOMAIN_IS_AVAILABLE', { domain: availableDomain })}
              </Text>
            )}
            <Box width="100%" gap={2} verticalAlign="middle">
              <Button
                fullWidth
                suffixIcon={<ArrowRight />}
                onClick={onContinueClicked}
                disabled={!availableDomain}
                size="large"
              >
                {t('DOMAIN_PAGE_CONTINUE_BUTTON')}
              </Button>
              <TextButton onClick={onSkipClicked} suffixIcon={<ChevronRightSmall />} size="small" skin="dark">
                {t('DOMAIN_PAGE_SKIP_BUTTON')}
              </TextButton>
            </Box>
          </Box>
        }
      />
    </Box>
  );
}
